<template>
    <div class="agent-home">
        <el-container v-loading="pageLoading" element-loading-text="加载中..."
            element-loading-background="rgba(0, 0, 0, 0.8)">
            <!-- 左侧菜单栏 -->
            <el-aside class="sidebar" :class="{ 'collapsed': isMenuCollapsed }">
                <!-- Logo区域 -->
                <div class="sidebar-logo">
                    <i class="el-icon-s-platform"></i>
                    <transition name="fade">
                        <span v-if="!isMenuCollapsed" class="logo-text">代理平台</span>
                    </transition>
                </div>
                <!-- 菜单 -->
                <el-menu :default-active="activeMenu" ref="sideMenu" :collapse="isMenuCollapsed"
                    :collapse-transition="false" background-color="#1a1f2e" text-color="rgba(255, 255, 255, 0.7)"
                    active-text-color="#409EFF" :unique-opened="true" @select="handleMenuSelect" class="sidebar-menu">
                    <template v-for="menuGroup in MENU_CONFIG">
                        <!-- 有子菜单的菜单项 -->
                        <el-submenu v-if="menuGroup.children && menuGroup.children.length > 0" :disabled="isMenuDisabled"
                            :key="'submenu-' + menuGroup.index" :index="menuGroup.index" class="menu-submenu">
                            <template slot="title">
                                <i :class="menuGroup.icon" class="menu-icon"></i>
                                <span class="menu-title">{{ menuGroup.title }}</span>
                            </template>
                            <el-menu-item v-for="menuItem in menuGroup.children" :key="menuItem.index"
                                :index="menuItem.index" class="menu-item" :disabled="isMenuDisabled">
                                <span class="menu-item-text">{{ menuItem.title }}</span>
                            </el-menu-item>
                        </el-submenu>
                        <!-- 无子菜单的一级菜单项 -->
                        <el-menu-item v-else :key="'menuitem-' + menuGroup.index" :index="menuGroup.index"
                            class="menu-item-single" :disabled="isMenuDisabled">
                            <i :class="menuGroup.icon" class="menu-icon"></i>
                            <span slot="title" class="menu-title">{{ menuGroup.title }}</span>
                        </el-menu-item>
                    </template>
                </el-menu>

                <!-- 折叠按钮 -->
                <div class="sidebar-toggle" @click="toggleSidebar">
                    <i :class="isMenuCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
                </div>
            </el-aside>

            <!-- 主内容区域 -->
            <el-container class="main-container" :class="{ 'sidebar-collapsed': isMenuCollapsed }">
                <el-header class="header">
                    <div class="header-left">
                        <el-breadcrumb separator-class="el-icon-arrow-right" class="breadcrumb">
                            <el-breadcrumb-item v-for="(item, index) in breadcrumbItems" :key="index"
                                @click.native="handleBreadcrumbClick(item, index)"
                                :class="{ 'is-clickable': item.route && index < breadcrumbItems.length - 1 }">
                                <i v-if="index === 0" class="el-icon-s-home"></i>
                                {{ item.title }}
                            </el-breadcrumb-item>
                        </el-breadcrumb>
                    </div>
                    <div class="header-right">
                        <!-- 通知图标 -->
                        <el-popover placement="bottom" width="300" trigger="click" popper-class="notification-popover">
                            <div class="notification-list">
                                <div class="notification-header">
                                    <span>消息通知</span>
                                    <el-link type="primary" :underline="false" class="mark-all-read">全部已读</el-link>
                                </div>
                                <div class="notification-items">
                                    <div class="notification-item" v-for="(item, index) in notificationList"
                                        :key="index">
                                        <div class="notification-dot" :class="{ 'unread': !item.read }"></div>
                                        <div class="notification-content">
                                            <p class="notification-title">{{ item.title }}</p>
                                            <p class="notification-time">{{ item.time }}</p>
                                        </div>
                                    </div>
                                    <div v-if="notificationList.length === 0" class="notification-empty">
                                        <i class="el-icon-bell"></i>
                                        <p>暂无新消息</p>
                                    </div>
                                </div>
                                <div class="notification-footer">
                                    <el-link type="primary" :underline="false">查看全部消息</el-link>
                                </div>
                            </div>
                            <el-badge :value="unreadCount" :hidden="unreadCount === 0" class="header-badge"
                                slot="reference">
                                <i class="el-icon-bell header-icon"></i>
                            </el-badge>
                        </el-popover>

                        <!-- 用户下拉菜单 -->
                        <el-dropdown @command="handleUserCommand" trigger="click" class="user-dropdown">
                            <div class="user-info">
                                <el-avatar class="user-avatar">
                                    <i class="el-icon-user-solid"></i>
                                </el-avatar>
                                <span class="user-name">{{ userInfo.userName || '用户' }}</span>
                                <i class="el-icon-arrow-down user-arrow"></i>
                            </div>
                            <el-dropdown-menu slot="dropdown" class="user-dropdown-menu">
                                <el-dropdown-item command="profile" icon="el-icon-user">
                                    <span class="dropdown-text">个人信息</span>
                                </el-dropdown-item>
                                <!-- <el-dropdown-item command="settings" icon="el-icon-setting">
                                    <span class="dropdown-text">系统设置</span>
                                </el-dropdown-item> -->
                                <el-dropdown-item divided command="logout" icon="el-icon-switch-button">
                                    <span class="dropdown-text">退出登录</span>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </div>
                </el-header>
                <el-main class="main-content">
                    <transition name="fade-transform" mode="out-in">
                        <div class="content-area" :key="$route.fullPath">
                            <router-view></router-view>
                        </div>
                    </transition>
                </el-main>
            </el-container>
        </el-container>

        <el-dialog :visible.sync="organizationPromptVisible" width="420px" :show-close="false"
            :close-on-click-modal="false" :close-on-press-escape="false" custom-class="organization-prompt-dialog"
            append-to-body>
            <div class="organization-prompt">
                <div class="organization-prompt__icon">
                    <i class="el-icon-warning-outline"></i>
                </div>
                <div class="organization-prompt__title">请先完善公司信息</div>
                <div class="organization-prompt__desc">完善公司信息后，才可继续进行后续代理业务操作。</div>
                <div class="organization-prompt__footer">
                    <el-button type="primary" @click="handleOrganizationPromptConfirm">去完善</el-button>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import Cookies from 'js-cookie'
import { getUserInfo } from '@/api/modules/user'
import { MENU_CONFIG, USER_COMMANDS, BREADCRUMB_CONFIG, FLOW_CONTEXT_MAP, SUB_PAGE_TITLE_MAP } from './constants'
import { subscribeNotice } from '@/utils/network/mqttSubscribe'
export default {
    data() {
        return {
            // 菜单状态
            activeMenu: this.getStoredActiveMenu(),
            isMenuCollapsed: this.getStoredCollapseState(),

            // 用户信息
            userInfo: {
                userName: '',
                roleId: null
            },

            // 通知消息
            notificationList: [
            ],
            unreadCount: 0,

            // 页面状态
            pageLoading: false,
            organizationPromptVisible: false,
            // 企业审核状态 1=审核通过，其他值下菜单不可点
            organizationStatus: null,

            // 菜单配置
            MENU_CONFIG
        }
    },
    computed: {
        // 侧边栏宽度
        sidebarWidth() {
            return this.isMenuCollapsed ? '64px' : '220px'
        },
        breadcrumbItems() {
            return this.getBreadcrumbByRoute() || this.generateBreadcrumbFromMenu(this.activeMenu) || BREADCRUMB_CONFIG.DEFAULT
        },
        // 企业审核未通过时禁用所有菜单
        isMenuDisabled() {
            return this.organizationStatus !== null && Number(this.organizationStatus) !== 1
        },
        channelCode() {
            return this.$channelMaterial.getChannelCode()
        },
    },
    mounted() {
        // 页面加载时默认跳转到工作台
        if (this.$route.path === '/agentHome') {
            this.$router.push('/agentHome/workbench')
        }
        this.updateMenuFromRoute()
        this.getUserInfo()
        this.subscribeMqNotice()
    },
    watch: {
        // 监听路由变化，自动更新菜单状态
        '$route'(to, from) {
            this.updateMenuFromRoute()
        }
    },

    methods: {
        async subscribeMqNotice() {
            this.unsubscribeMqNotice = await subscribeNotice(this.channelCode)
        },
        // 获取存储的菜单状态
        getStoredActiveMenu() {
            try {
                return localStorage.getItem('agentActiveMenu') || null
            } catch (error) {
                console.warn('获取菜单状态失败:', error)
                return null
            }
        },

        // 保存菜单状态
        saveActiveMenu(menuIndex) {
            try {
                localStorage.setItem('agentActiveMenu', menuIndex)
            } catch (error) {
                console.warn('保存菜单状态失败:', error)
            }
        },

        // 获取侧边栏折叠状态
        getStoredCollapseState() {
            try {
                const collapsed = localStorage.getItem('agentSidebarCollapsed')
                return collapsed === 'true'
            } catch (error) {
                return false
            }
        },

        // 切换侧边栏
        toggleSidebar() {
            this.isMenuCollapsed = !this.isMenuCollapsed
            try {
                localStorage.setItem('agentSidebarCollapsed', this.isMenuCollapsed)
            } catch (error) {
                console.warn('保存侧边栏状态失败:', error)
            }
        },

        // 根据当前路由匹配菜单项
        matchMenuByRoute() {
            const currentPath = this.$route.path
            const currentQuery = this.$route.query
            // 遍历所有菜单项，找到匹配的菜单
            for (const menuGroup of MENU_CONFIG) {
                // 检查一级菜单项（无子菜单）
                if (menuGroup.route && !menuGroup.children) {
                    const [routePath, queryString] = menuGroup.route.split('?')
                    if (currentPath === routePath) {
                        if (queryString) {
                            const menuQuery = new URLSearchParams(queryString)
                            let queryMatch = true
                            for (const [key, value] of menuQuery.entries()) {
                                if (currentQuery[key] !== value) {
                                    queryMatch = false
                                    break
                                }
                            }
                            if (queryMatch) {
                                return menuGroup.index
                            }
                        } else {
                            return menuGroup.index
                        }
                    }
                }
                // 检查子菜单项
                if (menuGroup.children) {
                    for (const menuItem of menuGroup.children) {
                        if (menuItem.route) {
                            const [routePath, queryString] = menuItem.route.split('?')
                            if (currentPath === routePath) {
                                if (queryString) {
                                    const menuQuery = new URLSearchParams(queryString)
                                    let queryMatch = true
                                    for (const [key, value] of menuQuery.entries()) {
                                        if (currentQuery[key] !== value) {
                                            queryMatch = false
                                            break
                                        }
                                    }
                                    if (queryMatch) {
                                        return menuItem.index
                                    }
                                } else {
                                    return menuItem.index
                                }
                            }
                        }
                    }
                }
            }
            return null
        },
        // 根据当前路由更新菜单状态
        updateMenuFromRoute() {
            const matchedMenuIndex = this.matchMenuByRoute()
            if (matchedMenuIndex) {
                this.activeMenu = matchedMenuIndex
                this.saveActiveMenu(matchedMenuIndex)
                // 手动更新菜单选中状态
                this.$nextTick(() => {
                    if (this.$refs.sideMenu) {
                        this.$refs.sideMenu.activeIndex = matchedMenuIndex
                    }
                })
            }
        },

        // 获取用户信息
        async getUserInfo() {
            try {
                this.pageLoading = true
                const res = await getUserInfo()
                if (res.code === 200) {
                    const { roleId, userName, organizationStatus,userId } = res.data
                    // 更新用户信息
                    this.userInfo = { roleId, userName }
                    // 缓存企业审核状态，控制菜单是否可用
                    this.organizationStatus = organizationStatus
                    // 设置Cookie
                    Cookies.set('roleId', roleId)
                    Cookies.set('userName', userName)
                       Cookies.set('userId', userId)
                    // 更新Store
                    this.$store.commit('changedRoleId', roleId)

                    if (res.data.organizationStatus == 0) {
                        setTimeout(() => {
                            this.organizationPromptVisible = true
                        }, 100)
                    }
                } else {
                    this.$message.error(res.message || '获取用户信息失败')
                }
            } catch (error) {
                console.error('获取用户信息失败:', error)
                this.$message.error('获取用户信息失败，请重试')
            } finally {
                this.pageLoading = false
            }
        },
        handleOrganizationPromptConfirm() {
            this.organizationPromptVisible = false
            this.handleMenuSelect('8-2')
        },
        handleUserCommand(command) {
            const { LOGOUT, PROFILE, SETTINGS } = USER_COMMANDS
            switch (command) {
                case LOGOUT:
                    this.handleLogout()
                    break
                case PROFILE:
                    this.handleProfile()
                    break
                case SETTINGS:
                    this.handleSettings()
                    break
                default:
                    console.warn('未知的用户命令:', command)
            }
        },

        // 处理个人信息
        handleProfile() {
            this.handleMenuSelect('8-2')
        },

        // 处理系统设置
        handleSettings() {
            this.$message.info('系统设置功能开发中...')
        },
        handleLogout() {
            try {
                // 删除指定Cookies
                Cookies.remove('token_agent');
                Cookies.remove('token_tenderee');
                localStorage.clear()
            } finally {
                this.$router.replace({
                    path: '/login',
                    query: { type: 'agent' }
                })
            }
        },
        // 处理菜单选择
        handleMenuSelect(index) {
            this.activeMenu = index
            this.saveActiveMenu(index)
            const targetRoute = this.findRouteByIndex(index)
            if (targetRoute) {
                this.navigateToRoute(targetRoute)
            } else {
                console.warn('未找到对应的路由:', index)
                this.$message.warning('页面不存在或正在开发中')
            }
        },

        // 根据索引查找路由
        findRouteByIndex(index) {
            for (const menuGroup of MENU_CONFIG) {
                // 检查是否是一级菜单项（无子菜单）
                if (menuGroup.index === index && menuGroup.route) {
                    return menuGroup.route
                }
                // 检查子菜单项
                if (menuGroup.children) {
                    const menuItem = menuGroup.children.find(item => item.index === index)
                    if (menuItem) {
                        return menuItem.route
                    }
                }
            }
            return null
        },

        // 安全的路由导航
        async navigateToRoute(route) {
            try {
                // 解析目标路由
                const [targetPath, queryString] = route.split('?')
                const targetQuery = {}
                if (queryString) {
                    const params = new URLSearchParams(queryString)
                    params.forEach((value, key) => {
                        targetQuery[key] = value
                    })
                }
                await this.$router.push({ path: targetPath, query: targetQuery })
            } catch (error) {
                // 处理路由重复导航等错误
                if (error.name !== 'NavigationDuplicated') {
                    console.error('路由跳转失败:', error)
                    this.$message.error('页面跳转失败，请重试')
                }
            }
        },
        // 处理面包屑点击事件
        handleBreadcrumbClick(item, index) {

            if (item.route && index < this.breadcrumbItems.length - 1) {
                // 如果点击的是首页（第一个面包屑），关闭所有菜单
                if (index === 0) {
                    // 清除菜单选中状态
                    this.activeMenu = null
                    this.saveActiveMenu('')
                    // 关闭所有展开的子菜单
                    this.$nextTick(() => {
                        if (this.$refs.sideMenu) {
                            this.$refs.sideMenu.openedMenus = []
                        }
                    })
                    // 跳转到待办列表
                    this.$router.push('/agentHome/workbench')
                } else {
                    // 其他面包屑正常跳转
                    this.navigateToRoute(item.route)
                }
            }
        },
        getBreadcrumbByRoute() {
            const currentPath = this.$route.path
            const currentQuery = this.$route.query || {}
            const configKeys = Object.keys(BREADCRUMB_CONFIG).filter(key => key !== 'DEFAULT')

            for (const configKey of configKeys) {
                const [configPath, queryString] = configKey.split('?')
                if (configPath !== currentPath) {
                    continue
                }

                if (!queryString) {
                    return BREADCRUMB_CONFIG[configKey]
                }

                const configQuery = new URLSearchParams(queryString)
                let isMatched = true
                for (const [key, value] of configQuery.entries()) {
                    if (String(currentQuery[key]) !== value) {
                        isMatched = false
                        break
                    }
                }

                if (isMatched) {
                    return BREADCRUMB_CONFIG[configKey]
                }
            }

            // 动态子页面：根据 procurementType + type 自动组装五级面包屑
            if (currentQuery.procurementType && currentQuery.type) {
                const contextKey = `${currentQuery.procurementType}_${currentQuery.type}`
                const context = FLOW_CONTEXT_MAP[contextKey]
                const pageTitle = SUB_PAGE_TITLE_MAP[currentPath]
                if (context && pageTitle) {
                    const q = currentQuery
                    const flowRoute = `${context.flowPath}?type=${q.type}&projectCode=${q.projectCode}&projectId=${q.projectId}&procurementType=${q.procurementType}`
                    return [
                        { title: '首页', route: '/agentHome' },
                        { title: context.module },
                        { title: context.type, route: context.typeRoute },
                        { title: context.flowTitle, route: flowRoute },
                        { title: pageTitle }
                    ]
                }
            }

            return null
        },
        // 根据menindex生成面包屑
        generateBreadcrumbFromMenu(menuIndex) {
            if (!menuIndex) {
                return [{ title: '首页', route: '/agentHome' }]
            }

            // 查找对应的菜单项
            for (const menuGroup of MENU_CONFIG) {
                // 检查是否是一级菜单项
                if (menuGroup.index === menuIndex && !menuGroup.children) {
                    return [
                        { title: '首页', route: '/agentHome' },
                        { title: menuGroup.title, route: menuGroup.route }
                    ]
                }

                // 检查子菜单项
                if (menuGroup.children) {
                    const menuItem = menuGroup.children.find(item => item.index === menuIndex)
                    if (menuItem) {
                        return [
                            { title: '首页', route: '/agentHome' },
                            { title: menuGroup.title, route: null },
                            { title: menuItem.title, route: menuItem.route }
                        ]
                    }
                }
            }

            // 如果没找到，返回默认面包屑
            return [{ title: '首页', route: '/agentHome' }]
        },

        // 根据项目类型生成面包屑（用于查看流程页面）
        generateBreadcrumbFromType(type) {
            const currentPath = this.$route.path

            // 模块和页面类型配置映射
            const moduleConfig = {
                gov: {
                    name: '政府采购',
                    types: {
                        '公开招标': '/agentHome/govOpenTendering',
                        '竞争性谈判': '/agentHome/govCompetitiveNegotiation',
                        '竞争性磋商': '/agentHome/govCompetitiveConsultation',
                        '邀请招标': '/agentHome/govInvitationTendering',
                        '询比价采购': '/agentHome/govInquiryProcurement',
                        '单一来源': '/agentHome/govSingleSource'
                    }
                },
                construction: {
                    name: '建设工程',
                    types: {
                        '公开招标': '/agentHome/constructionOpenTendering'
                    }
                },
                // semiProcess: {
                //     name: '半流程',
                //     types: {
                //         '发布招标公告': '/agentHome/semiProcess'
                //     }
                // },
                default: {
                    name: '企业采购',
                    types: {
                        '公开招标': '/agentHome/openTendering',
                        '竞争性谈判': '/agentHome/competitiveNegotiation',
                        '竞争性磋商': '/agentHome/competitiveConsultation',
                        '邀请招标': '/agentHome/invitationTendering',
                        '询比价采购': '/agentHome/inquiryProcurement',
                        '单一来源': '/agentHome/singleSource'
                    }
                }
            }

            // 确定当前模块
            let currentModuleKey = 'default'
            if (currentPath.includes('/gov')) {
                currentModuleKey = 'gov'
            } else if (currentPath.includes('/construction')) {
                currentModuleKey = 'construction'
            } else if (currentPath.includes('/semiProcess')) {
                currentModuleKey = 'semiProcess'
            }

            const module = moduleConfig[currentModuleKey]
            let currentPage = Object.keys(module.types)[0]
            let currentRoute = module.types[currentPage]

            // 根据 type 参数匹配页面类型
            if (type) {
                for (const [pageType, route] of Object.entries(module.types)) {
                    if (type.includes(pageType)) {
                        currentPage = pageType
                        currentRoute = route
                        break
                    }
                }
            }

            return [
                { title: '首页', route: '/agentHome' },
                { title: module.name, route: null },
                { title: currentPage, route: currentRoute },
                { title: '查看流程', route: null }
            ]
        }
    }
}
</script>

<style>
/* ==================== 全局样式 ==================== */
.agent-home {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    min-height: 100vh;
}
/* ==================== 侧边栏样式 ==================== */
.sidebar {
    background: linear-gradient(180deg, #1a1f2e 0%, #0f1419 100%);
    height: 100vh;
    width: 220px !important;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: fixed;
    left: 0;
    top: 0;
    z-index: 100;
    overflow: hidden;
}

/* 侧边栏折叠状态 */
.sidebar.collapsed {
    width: 64px !important;
}

/* Logo区域 */
.sidebar-logo {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
    background: rgba(255, 255, 255, 0.05);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    gap: 12px;
}

.sidebar-logo i {
    font-size: 28px;
    color: #409EFF;
    transition: transform 0.3s ease;
}


.logo-text {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    letter-spacing: 1px;
}

/* 菜单样式 */
.sidebar-menu {
    border: none;
    height: calc(100vh - 124px);
    overflow-y: auto;
    overflow-x: hidden;
}

.sidebar-menu::-webkit-scrollbar {
    width: 6px;
}

.sidebar-menu::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
}

.sidebar-menu::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
}

/* 菜单项样式 */
.menu-icon {
    font-size: 18px;
    margin-right: 12px;
    transition: all 0.3s ease;
}

.menu-title {
    font-size: 14px;
    font-weight: 500;
}

.menu-item-text {
    font-size: 13px;
    padding-left: 8px;
}

/* 菜单项悬停效果 */
.sidebar-menu>>>.el-menu-item:hover,
.sidebar-menu>>>.el-submenu__title:hover {
    background: rgba(64, 158, 255, 0.1) !important;
    color: #409EFF !important;
}

.sidebar-menu>>>.el-menu-item:hover .menu-icon,
.sidebar-menu>>>.el-submenu__title:hover .menu-icon {
    transform: scale(1.1);
    color: #409EFF;
}

/* 激活状态 */
.sidebar-menu>>>.el-menu-item.is-active {
    background: linear-gradient(90deg, rgba(64, 158, 255, 0.2) 0%, transparent 100%) !important;
    border-right: 3px solid #409EFF;
    color: #409EFF !important;
}

.sidebar-menu>>>.el-menu-item.is-active .menu-icon {
    color: #409EFF;
    transform: scale(1.15);
}

/* 子菜单样式 */
.sidebar-menu>>>.el-submenu .el-menu-item {
    min-width: 0;
    padding-left: 60px !important;
    background: rgba(0, 0, 0, 0.1);
}

.sidebar-menu>>>.el-submenu .el-menu-item:hover {
    background: rgba(64, 158, 255, 0.15) !important;
}

/* 折叠按钮 */
.sidebar-toggle {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.2);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
}

.sidebar-toggle:hover {
    background: rgba(64, 158, 255, 0.2);
}

.sidebar-toggle i {
    font-size: 20px;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;
}

.sidebar-toggle:hover i {
    color: #409EFF;
    transform: scale(1.2);
}

/* ==================== 主容器样式 ==================== */
.main-container {
    display: flex;
    flex-direction: column;
    margin-left: 220px;
    height: 100vh;
    overflow: hidden;
    background: #f0f2f5;
    transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0;
}

/* 侧边栏折叠时调整主容器位置 */
.main-container.sidebar-collapsed {
    margin-left: 64px;
}

/* ==================== Header样式 ==================== */
.header {
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px !important;
    padding: 0 24px;
    flex-shrink: 0;
    z-index: 10;
    position: relative;
}

.header-left {
    flex: 1;
    display: flex;
    align-items: center;
}

/* 面包屑样式 */
.breadcrumb {
    font-size: 14px;
}

.breadcrumb>>>.el-breadcrumb__item {
    display: flex;
    align-items: center;
}

.breadcrumb>>>.el-breadcrumb__inner {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #606266;
    font-weight: 400;
    transition: all 0.3s ease;
}

.breadcrumb>>>.el-breadcrumb__inner i {
    font-size: 16px;
}

.breadcrumb .is-clickable>>>.el-breadcrumb__inner {
    cursor: pointer;
}

.breadcrumb .is-clickable>>>.el-breadcrumb__inner:hover {
    color: #409EFF;
}

.breadcrumb>>>.el-breadcrumb__item:last-child .el-breadcrumb__inner {
    color: #303133;
    font-weight: 600;
}

/* Header右侧 */
.header-right {
    display: flex;
    align-items: center;
    gap: 24px;
}

.header-badge {
    cursor: pointer;
}

.header-icon {
    font-size: 20px;
    color: #606266;
    cursor: pointer;
    transition: all 0.3s ease;
}

.header-icon:hover {
    color: #409EFF;
    transform: scale(1.1);
}

/* 用户信息 */
.user-dropdown {
    cursor: pointer;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 16px;
    border-radius: 20px;
    transition: all 0.3s ease;
}

.user-info:hover {
    background: #f5f7fa;
}

.user-avatar {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.user-name {
    font-size: 14px;
    color: #303133;
    font-weight: 500;
}

.user-arrow {
    font-size: 12px;
    color: #909399;
    transition: transform 0.3s ease;
}

.user-dropdown:hover .user-arrow {
    transform: rotate(180deg);
}

/* 通知下拉菜单样式 */
.notification-popover {
    padding: 0 !important;
}

.notification-list {
    max-height: 320px;
    display: flex;
    flex-direction: column;
}

.notification-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
}

.mark-all-read {
    font-size: 12px;
}

.notification-items {
    flex: 1;
    overflow-y: auto;
    max-height: 220px;
}

.notification-item {
    display: flex;
    align-items: flex-start;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f2f5;
    cursor: pointer;
    transition: background 0.2s ease;
}

.notification-item:hover {
    background: #f5f7fa;
}

.notification-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #c0c4cc;
    margin-top: 6px;
    margin-right: 10px;
    flex-shrink: 0;
}

.notification-dot.unread {
    background: #f56c6c;
}

.notification-content {
    flex: 1;
    min-width: 0;
}

.notification-title {
    margin: 0;
    font-size: 13px;
    color: #303133;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.notification-time {
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #909399;
}

.notification-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #909399;
}

.notification-empty i {
    font-size: 32px;
    margin-bottom: 8px;
}

.notification-empty p {
    margin: 0;
    font-size: 13px;
}

.notification-footer {
    padding: 10px 16px;
    text-align: center;
    border-top: 1px solid #ebeef5;
}

/* 下拉菜单样式 */
.user-dropdown-menu {
    margin-top: 8px !important;
}

.dropdown-text {
    font-size: 14px;
}

/* ==================== 主内容区域 ==================== */
.main-content {
    background: #f0f2f5;
    padding: 20px;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    height: calc(100vh - 64px);
    padding: 0;
}

.content-area {
    width: 100%;
    box-sizing: border-box;
    padding: 10px;
}

.content-area::-webkit-scrollbar {
    width: 8px;
}

.content-area::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 4px;
}

.content-area::-webkit-scrollbar-thumb:hover {
    background: #c0c4cc;
}

/* ==================== 动画效果 ==================== */
/* 淡入淡出 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* 页面切换动画 */
.fade-transform-enter-active,
.fade-transform-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-transform-enter {
    opacity: 0;
    transform: translateX(20px);
}

.fade-transform-leave-to {
    opacity: 0;
    transform: translateX(-20px);
}

/* ==================== 公司信息提示弹窗 ==================== */
.organization-prompt-dialog {
    border-radius: 16px;
}

.organization-prompt-dialog .el-dialog__header {
    display: none;
}

.organization-prompt {
    text-align: center;
    padding: 12px 8px 4px;
}

.organization-prompt__icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px;
    border-radius: 50%;
    background: rgba(230, 162, 60, 0.12);
    color: #e6a23c;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
}

.organization-prompt__title {
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 10px;
}

.organization-prompt__desc {
    font-size: 14px;
    line-height: 22px;
    color: #6b7280;
    margin-bottom: 24px;
}

.organization-prompt__footer {
    display: flex;
    justify-content: center;
}

.organization-prompt__footer .el-button {
    min-width: 120px;
    border-radius: 999px;
}
</style>
